import type { SituacaoDaOs } from '../../types/ordemDeServico'

/**
 * O que uma situação diz ao fiscal.
 *
 * @property rotulo o nome curto, na etiqueta e no filtro
 * @property esperando de quem o sistema está esperando o próximo ato — a pergunta que trava o
 *     processo, e a que a etiqueta sozinha não responde
 * @property ajuda a frase que traduz a etiqueta para quem não é de TI
 * @property cor as classes da etiqueta
 */
export interface DescricaoDaSituacao {
  rotulo: string
  esperando: string
  ajuda: string
  cor: string
}

/**
 * As sete situações, todas escritas — **sem `default` e sem cinza de reserva**.
 *
 * O sistema anterior mapeava rótulo e cor para 4 dos 13 estados de GMUD e mandava os outros nove
 * para um cinza com "?": ninguém percebeu porque a tela não quebrava. O `Record` sobre a união é o
 * que cobra a entrada de toda situação nova — se o servidor acrescentar uma, o build para aqui em
 * vez de a tela cair num rótulo vazio.
 *
 * A situação **não é gravada**: o servidor a projeta dos fatos registrados, na precedência que
 * `SituacaoDaOs` declara. Mudar a situação de uma ordem é registrar um fato, que carrega autor,
 * data, meio e referência — nunca escolher um valor numa caixa.
 *
 * **Nenhuma delas se chama "atrasada".** Atraso depende de prazo, o prazo depende da entrega ao
 * preposto (6.24.6, p.45), e quando ela falta o sistema diz "não apurável" na aba de prazos em vez
 * de acusar quem não tinha como cumprir.
 */
export const SITUACAO_DA_OS: Record<SituacaoDaOs, DescricaoDaSituacao> = {
  REVOGADA: {
    rotulo: 'Revogada',
    esperando: 'ninguém',
    ajuda:
      'A ordem foi retirada, com quem retirou, quando e por quê. Ela continua registrada e não ' +
      'autoriza mais nada.',
    cor: 'bg-slate-200 text-slate-900 ring-slate-400',
  },
  AGUARDANDO_ENTREGA: {
    rotulo: 'Aguardando entrega ao preposto',
    esperando: 'a Prefeitura',
    ajuda:
      'A ordem foi emitida e ainda não consta que tenha sido entregue ao representante da contratada. ' +
      'Não é atraso: é prazo que ainda não começou a correr.',
    cor: 'bg-amber-50 text-amber-900 ring-amber-200',
  },
  EM_EXECUCAO: {
    rotulo: 'Em execução',
    esperando: 'a contratada',
    ajuda: 'A ordem chegou ao preposto e o prazo de execução corre desde o dia útil seguinte.',
    cor: 'bg-sky-50 text-sky-900 ring-sky-200',
  },
  ENCERRADA_AGUARDANDO_TRP: {
    rotulo: 'Encerrada, aguardando recebimento provisório',
    esperando: 'a Prefeitura',
    ajuda:
      'A contratada encerrou a ordem. O prazo do termo de recebimento provisório corre contra a ' +
      'Prefeitura, e não contra ela.',
    cor: 'bg-indigo-50 text-indigo-900 ring-indigo-200',
  },
  AGUARDANDO_TRD: {
    rotulo: 'Aguardando recebimento definitivo',
    esperando: 'a Prefeitura',
    ajuda:
      'Saiu ao menos uma peça do termo de recebimento provisório. O prazo do definitivo conta da ' +
      'última delas.',
    cor: 'bg-indigo-50 text-indigo-900 ring-indigo-200',
  },
  RECEBIDA_DEFINITIVAMENTE: {
    rotulo: 'Recebida definitivamente',
    esperando: 'ninguém',
    ajuda:
      'O recebimento definitivo aconteceu — por termo assinado ou pelo esgotamento do prazo, que o ' +
      'contrato trata como recebimento tácito.',
    cor: 'bg-green-50 text-green-900 ring-green-200',
  },
  FATURAMENTO_AUTORIZADO: {
    rotulo: 'Faturamento autorizado',
    esperando: 'a contratada',
    ajuda: 'O gestor avisou o preposto de que a nota fiscal pode ser emitida.',
    cor: 'bg-green-50 text-green-900 ring-green-200',
  },
}

/**
 * As situações na ordem em que o filtro as oferece: do começo do ciclo para o fim, revogada por último.
 *
 * Sai de `Object.keys` e não de uma segunda lista escrita à mão — duas listas divergem, e a que
 * divergiria seria justamente esta, que ninguém relê. O mapa acima é declarado nessa ordem de
 * leitura, que **não** é a ordem de precedência do servidor (lá a revogação vem primeiro, porque
 * ordem revogada não autoriza mais nada): a precedência decide qual situação vale, e esta lista só
 * decide em que ordem o filtro as apresenta.
 */
export const SITUACOES_NA_ORDEM_DO_CICLO: SituacaoDaOs[] = [
  ...(Object.keys(SITUACAO_DA_OS) as SituacaoDaOs[]).filter((situacao) => situacao !== 'REVOGADA'),
  'REVOGADA',
]

/**
 * @param situacao a situação projetada
 * @returns como ela se apresenta na tela
 */
export function descricaoDaSituacao(situacao: SituacaoDaOs): DescricaoDaSituacao {
  return SITUACAO_DA_OS[situacao]
}
